import { Box, Dialog, Stack, Typography } from '@mui/material';
import { IconButton } from '@tix-ticketing/ui-components';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CrossIcon,
} from '@tix-ticketing/ui-icons/outline';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { TicketDto } from 'api/generated';
import { useAppState } from 'lib/app-state';
import { FormattedPrice } from 'lib/helpers/formatted-price';

export const Seat360Dialog = ({
  ticket,
  hallName,
  onClose,
}: {
  ticket: TicketDto;
  hallName: string;
  onClose: () => void;
}) => {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDivElement>(null);
  const mouseDownRef = useRef(false);

  // TODO probably needs to work as an order as well though (instead of just relying tempEventReservationData)?

  const { tempEventReservationData } = useAppState();
  const allTickets = tempEventReservationData?.tickets.filter((t) => t.path360);
  const [selectedTicket, setSelectedTicket] = useState<TicketDto>(ticket);

  const prevTicket = () => {
    if (!allTickets) return;
    const currentIndex = allTickets.findIndex(
      (t) => t.id === selectedTicket.id
    );
    const prevIndex =
      (currentIndex - 1 + allTickets.length) % allTickets.length;
    setSelectedTicket(allTickets[prevIndex] as TicketDto);
  };

  const nextTicket = () => {
    if (!allTickets) return;
    const currentIndex = allTickets.findIndex(
      (t) => t.id === selectedTicket.id
    );
    const nextIndex = (currentIndex + 1) % allTickets.length;
    setSelectedTicket(allTickets[nextIndex] as TicketDto);
  };

  // Move focus out of iframe for keyboard events to work
  useEffect(() => {
    let interval: NodeJS.Timeout;

    const start = () => {
      interval = setInterval(() => {
        if (!mouseDownRef.current) {
          dialogRef.current?.focus();
        }
      }, 500);
    };

    start();
    return () => clearInterval(interval);
  }, []);

  return (
    <Dialog open fullScreen onClose={onClose} ref={dialogRef} tabIndex={-1}>
      <IconButton
        color="white"
        variant="contained"
        sx={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
        }}
        onClick={onClose}
        label={intl.formatMessage({
          id: 'common.close',
          defaultMessage: 'Close',
        })}
      >
        <CrossIcon />
      </IconButton>
      <Box
        component="iframe"
        src={selectedTicket?.path360 ?? ''}
        title="360° Seat View"
        sx={{
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
      <Stack
        direction="row"
        sx={[
          {
            alignItems: 'center',
            gap: 2,
          },
          (theme) => ({
            position: 'absolute',
            bottom: '1rem',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: '1rem 1.5rem',
            // A px string: a bare number in `sx` is scaled by shape.borderRadius.
            borderRadius: `${theme.buttonRadius.main}px`,
            [theme.breakpoints.down('sm')]: {
              borderRadius: `${theme.buttonRadius.mobile}px`,
            },
            background: theme.palette.background.paper,
          }),
        ]}
      >
        {allTickets && allTickets.length > 1 && (
          <IconButton
            variant="contained"
            color="monochrome"
            onClick={prevTicket}
            label={intl.formatMessage({
              id: 'common.previous',
              defaultMessage: 'Previous',
            })}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
        {hallName && (
          <TextBlock
            title={intl.formatMessage({
              id: 'seatPicker.hall',
              defaultMessage: 'Hall',
            })}
            description={hallName}
            alignment="left"
          />
        )}
        {selectedTicket.section && (
          <TextBlock
            title={intl.formatMessage({
              id: 'common.section',
              defaultMessage: 'Section',
            })}
            description={selectedTicket.section}
          />
        )}
        {selectedTicket.row && (
          <TextBlock
            title={intl.formatMessage({
              id: 'seatPicker.row',
              defaultMessage: 'Row',
            })}
            description={selectedTicket.row}
          />
        )}
        {selectedTicket.seat && (
          <TextBlock
            title={intl.formatMessage({
              id: 'seatPicker.seat',
              defaultMessage: 'Seat',
            })}
            description={selectedTicket.seat}
          />
        )}
        <TextBlock
          title={intl.formatMessage({
            id: 'common.price',
            defaultMessage: 'Price',
          })}
          description={<FormattedPrice amount={selectedTicket.price ?? 0} />}
          alignment="right"
        />
        {allTickets && allTickets.length > 1 && (
          <IconButton
            variant="contained"
            color="monochrome"
            onClick={nextTicket}
            label={intl.formatMessage({
              id: 'common.next',
              defaultMessage: 'Next',
            })}
          >
            <ArrowRightIcon />
          </IconButton>
        )}
      </Stack>
    </Dialog>
  );
};

const TextBlock = ({
  title,
  description,
  alignment = 'center',
}: {
  title: string;
  description: string | React.ReactNode;
  alignment?: 'left' | 'center' | 'right';
}) => {
  return (
    <Box
      sx={{
        px: 3,
      }}
    >
      <Typography
        variant="body3"
        align={alignment}
        sx={{
          color: 'text.secondary',
        }}
      >
        {title}
      </Typography>
      <Typography variant="h6" align={alignment}>
        {description}
      </Typography>
    </Box>
  );
};
